import { useState } from 'react';
import { Loader2, MonitorSmartphone, ShieldOff, X } from 'lucide-react';
import { useTrustedDevices, useRevokeTrustedDevices, useRevokeTrustedDevice } from '../../hooks/useAdminData';
import type { AdminUser } from '../../types';

/**
 * Remembered browsers for one user ("Husk denne enheten" on the 2FA screen).
 *
 * Collapsed, this is a count badge. Expanded, it lists each remembered browser with a
 * per-device revoke, plus "revoke all". Revocation is confirmed inline rather than with
 * a browser dialog so it reads as part of the page and cannot be dismissed by accident.
 */
export function TrustedDevicesPanel({ user }: { user: AdminUser }) {
  const [open, setOpen] = useState(false);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const username = user.userName ?? '';
  const { data, isLoading } = useTrustedDevices(username, open);
  const revokeAll = useRevokeTrustedDevices();
  const revokeOne = useRevokeTrustedDevice();

  const count = user.trustedDeviceCount ?? 0;
  const devices = data?.devices ?? [];
  const busy = revokeAll.isPending || revokeOne.isPending;

  const handleRevokeAll = async () => {
    setMessage(null);
    try {
      await revokeAll.mutateAsync(username);
      setMessage('Alle huskede enheter er tilbakekalt. Neste innlogging krever engangskode.');
    } catch {
      setMessage('Kunne ikke tilbakekalle enhetene. Prøv igjen.');
    } finally {
      setConfirmingAll(false);
    }
  };

  const handleRevokeOne = async (deviceId: string) => {
    setMessage(null);
    try {
      await revokeOne.mutateAsync({ username, deviceId });
      setMessage('Enheten er tilbakekalt.');
    } catch {
      setMessage('Kunne ikke tilbakekalle enheten. Prøv igjen.');
    } finally {
      setConfirmingId(null);
    }
  };

  return (
    <div className="text-sm">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full transition-colors ${
          count > 0
            ? 'bg-amber-50 text-amber-800 hover:bg-amber-100'
            : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
        }`}
        title="Nettlesere som slipper engangskode ved innlogging"
      >
        <MonitorSmartphone size={14} />
        {count === 1 ? '1 husket enhet' : `${count} huskede enheter`}
      </button>

      {open && (
        <div className="mt-2 w-96 max-w-full bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
          {isLoading ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 size={14} className="animate-spin" /> Laster enheter...
            </div>
          ) : devices.length === 0 ? (
            <p className="text-slate-500">Ingen huskede enheter.</p>
          ) : (
            <ul className="divide-y divide-slate-200">
              {devices.map((d) => (
                <li key={d.id} className="py-1.5 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-slate-800 truncate" title={d.userAgent ?? undefined}>
                      {describeUserAgent(d.userAgent)}
                    </p>
                    <p className="text-xs text-slate-500">
                      Sist brukt {formatDate(d.lastUsedUtc)} · utløper {formatDate(d.expiresUtc)}
                    </p>
                  </div>
                  {confirmingId === d.id ? (
                    <span className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleRevokeOne(d.id)}
                        disabled={busy}
                        className="px-2 py-0.5 rounded bg-red-600 text-white text-xs hover:bg-red-700 disabled:opacity-50"
                      >
                        Tilbakekall
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmingId(null)}
                        disabled={busy}
                        className="px-1.5 py-0.5 rounded text-slate-500 hover:bg-slate-200"
                        aria-label="Avbryt"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmingId(d.id)}
                      disabled={busy}
                      className="shrink-0 text-xs text-red-700 hover:text-red-800 disabled:opacity-50"
                    >
                      Tilbakekall
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {message && (
            <p role="status" className="text-xs text-slate-600">
              {message}
            </p>
          )}

          {devices.length > 1 && !confirmingAll && (
            <button
              type="button"
              onClick={() => setConfirmingAll(true)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-red-700 hover:text-red-800 disabled:opacity-50"
            >
              <ShieldOff size={14} /> Tilbakekall alle enheter
            </button>
          )}

          {confirmingAll && (
            <div className="flex items-center gap-2">
              <span className="text-slate-700">Sikker? Brukeren må skrive inn kode igjen.</span>
              <button
                type="button"
                onClick={handleRevokeAll}
                disabled={busy}
                className="px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {revokeAll.isPending ? 'Tilbakekaller...' : 'Ja, tilbakekall alle'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingAll(false)}
                disabled={busy}
                className="px-2 py-1 rounded text-slate-600 hover:bg-slate-200"
              >
                Avbryt
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '–' : d.toLocaleDateString('nb-NO');
}

/** A readable label from a raw User-Agent; the full string stays in the tooltip. */
function describeUserAgent(ua: string | null): string {
  if (!ua) return 'Ukjent nettleser';
  const browser =
    /Edg\//.test(ua) ? 'Edge' :
    /OPR\//.test(ua) ? 'Opera' :
    /Chrome\//.test(ua) ? 'Chrome' :
    /Firefox\//.test(ua) ? 'Firefox' :
    /Safari\//.test(ua) ? 'Safari' : 'Nettleser';
  const os =
    /Windows/.test(ua) ? 'Windows' :
    /Android/.test(ua) ? 'Android' :
    /iPhone|iPad/.test(ua) ? 'iOS' :
    /Mac OS/.test(ua) ? 'macOS' :
    /Linux/.test(ua) ? 'Linux' : '';
  return os ? `${browser} på ${os}` : browser;
}

export default TrustedDevicesPanel;
